import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import api from 'config/axiosConfig'
import IssueCard from 'components/Issues/IssueCard'
import EntitiesSort from 'components/Entities/EntitiesSort'
import Comment from 'components/Comments/Comment'
import { SavedEntity, Sort } from 'util/constants'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import ScrollHideContainer from 'ui/ScrollHideContainer'
import { Loader2 } from 'lucide-react'
import { apiRoutes } from "util/apiRoutes";
import { PostFullRenderer } from "components/Posts/display/PostRendererRegistry";
import { resolvePostKind } from "components/Posts/display/resolvePostKind";
const UserProfileSaved: React.FC = () => {
    const { currentUser, awaitingUser } = useUser()
    const [searchParams, setSearchParams] = useSearchParams()
    const [savedEntities, setSavedEntities] = useState<SavedEntity[]>([])
    const [loading, setLoading] = useState(true)
    const [usernameAllowed, setUsernameAllowed] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [offset, setOffset] = useState(0)
    const [hasMore, setHasMore] = useState(true)
    const { username } = useParams<{ username: string }>()
    const limit = 10

    const sort = (searchParams.get('sort') as Sort) || 'new'
    
    useEffect(() => {
        if (awaitingUser) {
            return
        }
        if (currentUser && currentUser?.username !== username) {
            setUsernameAllowed(false)
        } else {
            setUsernameAllowed(true)
        }
        if (currentUser?.username) {
            fetchUserSavedEntities(true)
        } else {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.username, sort])

    if (!usernameAllowed) {
        return (
            <div className="flex flex-col items-center gap-2 py-4">
                <h4 className="text-eoe-red dark:text-eoe-dark-text">
                    You do not have permission to access this page. {username}
                </h4>
                <Link to={`/user/${currentUser?.username}/saved`} className="btn-primary">
                    View Your Saved Items
                </Link>
            </div>
        )
    }

    const fetchUserSavedEntities = async (reset = false) => {
        try {
            const response = await api.get(apiRoutes.saves.root, {
                params: {
                    sortField: getSortField(sort),
                    sortOrder: getSortOrder(sort),
                    offset: reset ? 0 : offset,
                    limit
                }
            })
            if (reset) {
                setSavedEntities(response.data)
                setOffset(limit)
            } else {
                setSavedEntities(prev => [...prev, ...response.data])
                setOffset(prev => prev + limit)
            }
            setHasMore(response.data.length === limit)
            setLoading(false)
        } catch (err) {
            if (err instanceof AxiosError && err.response?.status === 404) {
                setError('404: Failed to load saved entities. Please try again later.')
            } else {
                setError('Failed to load saved entities. Please try again later.')
            }
            setLoading(false)
        }
    }

    const getSortField = (sortValue: Sort): 'saved_ts' | 'vote_count' => {
        switch (sortValue) {
            case 'top':
            case 'bottom':
                return 'vote_count'
            case 'new':
            case 'old':
                return 'saved_ts'
            default:
                return 'saved_ts'
        }
    }

    const getSortOrder = (sortValue: Sort): 'DESC' | 'ASC' => {
        switch (sortValue) {
            case 'top':
            case 'new':
                return 'DESC'
            case 'bottom':
            case 'old':
                return 'ASC'
            default:
                return 'DESC'
        }
    }

    const handleSortChange = (newSort: Sort) => {
        setSearchParams({ sort: newSort })
        setOffset(0)
        setSavedEntities([])
    }

    const loadMore = () => {
        fetchUserSavedEntities()
    }

    const renderEntityCard = (entity: SavedEntity) => {
        switch (entity.entity_type) {
            case 'issue':
                return (
                    <IssueCard
                        key={entity.eid}
                        issue={entity.attributes}
                        showMeta={true}
                        showActions={true}
                        truncateDescription={true}
                        isIssueProfilePage={false}
                    />
                )
            case 'comment':
                return (
                    <Comment
                        key={entity.eid}
                        {...entity.attributes}
                        depth={0}
                        maxDepth={0}
                        isAllCollapsed={true}
                        showActions={true}
                        onAddReply={() => {}}
                        onUpdateComment={() => {}}
                        onDeleteComment={() => {}}
                        commentable_type={entity.attributes.commentable_type}
                        commentable_id={entity.attributes.commentable_id}
                    />
                )
            default:
                if (resolvePostKind(entity.entity_type, entity.attributes)) {
                    return (
                        <PostFullRenderer
                            key={entity.eid}
                            entity={entity.attributes}
                            entityType={entity.entity_type}
                            context="saved"
                        />
                    )
                }

                return null
        }
    }

    if (loading) {
        return (
            <LoadingSkeleton variant="entity-card" count={limit} />
        )
    }

    if (error) {
        return error.match(/404/) ? <NotFoundPage /> : <ServerErrorPage />
    }

    return (
        <div className="relative">
            <ScrollHideContainer 
                className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
                hideOffset={114}
                threshold={110}
            >
                <div className="flex mx-2 gap-1 items-center">
                    <EntitiesSort entityType="saved" sort={sort} onSortChange={handleSortChange} />
                </div>
            </ScrollHideContainer>
            <div className="flex flex-col entity-divider">
                {savedEntities.map((entity) => renderEntityCard(entity))}
            </div>
            {hasMore && (
                <div className="flex justify-center m-2">
                    <button
                        onClick={loadMore}
                        className={`my-2 w-full py-0 ${loading ? 'btn-primary-disabled' : 'btn-primary'}`}
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="size-4 animate-spin" /> : 'Load more'}
                    </button>
                </div>
            )}
        </div>
    )
}

export default UserProfileSaved